import { Injectable } from '@angular/core';
import { ApiService } from '../../../services/apiservice/apiservice.service';
import { ConfigService } from '../../config/config.service';
import { Observable } from 'rxjs';

export interface Download {
  reserveId: string;
  formatType: string;
  links: DownloadLink;
  errorCode: string;
  message: string;
}

interface DownloadLink {
  self: LinkInfo;
  contentLink: LinkInfo;
}

interface LinkInfo {
  href: string;
  type: string;
}

export interface Checkout {
  reserveId: string;
  expires: string;
  formats: CheckoutFormats[];
  links: LinkMetaData;
  actions: CheckoutAction;
  checkoutDate: string;
}

interface CheckoutFormats {
  reserveId: string;
  formatType: string;
  link: ILink;
  linkTemplate: CheckoutFormatsTemplatesLink;
}
interface ILink {
  self: LinkInfo;
}

interface CheckoutFormatsTemplatesLink {
  downloadLink: LinkInfo;
  downloadLinkV2: LinkInfo;
}

interface LinkMetaData {
  self: LinkInfo;
  metaData: LinkInfo;
}

interface CheckoutAction {
  format: ActionItem;
  earlyReturn: ActionInfo;
}

interface ActionItem {
  href: string;
  type: string;
  method: string;
  fields: ActionFields[];
}

interface ActionInfo {
  href: string;
  method: string;
}

interface ActionFields {
  name: string;
  value: string;
  optional: string;
  options: string[];
}

export interface OverDriveItem {
  author: string;
  title: string;
  availability: Availability;
  isReserved: boolean;
  isCheckedOut: boolean;
  samples: SampleSource[];
  isReadyForPickup: boolean;
  formatDetails: FormatDetail[];
  magazineIssues?: MagazineIssues;
  reservePosition: number;
  productId: string;
  downloadRedirectLink?: string;
}

export interface MagazineIssues {
  id: string;
  limit: number;
  links: MagazineIssuesLinks;
  offset: number;
  totalItems: number;
  products: MagazineProduct[];
}

export interface MagazineProduct {
  contentDetails?: ContentDetails;
  crossRefId?: string;
  downloadRedirectLink?: string;
  edition?: string;
  formats?: MagazineProductFormat[];
  id?: string;
  images?: MagazineProductImages;
  isCheckedOut?: boolean;
  links?: MagazineProductLinks;
  mediaType?: string;
  series?: string;
  sortTitle?: string;
  starRating?: number;
  title?: string;
}

interface MagazineProductLinks {
  availability: LinkInfo;
  issues: LinkInfo;
  metaData: LinkInfo;
  self: LinkInfo;
}

interface MagazineProductImages {
  cover: LinkInfo;
  cover150Wide: LinkInfo;
  cover300Wide: LinkInfo;
  thumbnail: LinkInfo;
}

interface MagazineProductFormat {
  id: string;
  identifiers: MagazineProductFormatIdentifier[];
}

interface MagazineProductFormatIdentifier {
  type: string;
  value: string;
  name: string;
}

interface ContentDetails {
  account: ContentDetailsAccount;
  href: string;
  type: string;
}

interface ContentDetailsAccount {
  id: number;
  name: string;
}

interface MagazineIssuesLinks {
  first: LinkInfo;
  last: LinkInfo;
  self: LinkInfo;
}
interface Availability {
  id?: any;
  available: boolean;
  copiesOwned: number;
  copiesAvailable: number;
  numberOfHolds: number;
}

interface SampleSource {
  source: string;
  url: string;
}

interface FormatDetail {
  id: string;
  name: string;
  fileName: string;
  identifiers: Identifier[];
  fileSize: number;
  partCount: number;
  onSaleDate: string;
  rights: Identifier[];
  samples: Identifier[];
  magazineIssues?: any;
}

interface Identifier {
  name: string;
  value: string;
}
export interface MagazineIssue {
  id: string;
  isCheckedOut: boolean;
  edition: string;
  downloadRedirectLink: string;
  images: MagazineImage;
}

interface MagazineImage {
  thumbnail: LinkInfo;
}

interface LinkInfo {
  href: string;
  type: string;
}

export interface OverDrivePlaceHold {
  reserveId: string;
  emailAddress: string;
  holdListPosition: number;
  numberOfHolds: number;
  holdSuspension: HoldSuspension;
  holdPlacedDate: string;
  links: LinkMetaData;
  actions: Action;
  numberOfSuspensionDays: number;
  isHoldSuspension: boolean;
}

interface HoldSuspension {
  SuspensionType: string;
  NumberOfDays: number;
  IsHoldSuspension: boolean;
}

interface LinkMetaData {
  self: LinkInfo;
  metaData: LinkInfo;
  downloadRedirect: LinkInfo;
}

interface Action {
  removeHold: ActionItem;
  format: ActionItem;
}

interface ActionItem {
  href: string;
  type: string;
  method: string;
  fields: ActionFields[];
}

export interface PlaceHold {
  productId: string;
  emailAddress: string;
}
@Injectable({
  providedIn: 'root',
})
export class OverDriveService {
  private _overDriveUrl: string;

  constructor(
    private _configService: ConfigService,
    private _apiService: ApiService,
  ) {
    this._overDriveUrl = `${this._configService.agApi}/econtent/overdrive`;
  }

  checkout(id: string): Observable<string> {
    return this._apiService.get(`${this._overDriveUrl}/checkout/${id}`);
  }

  cancelHold(productId: string): Observable<void> {
    return this._apiService.delete(`${this._overDriveUrl}/removehold/${productId}`);
  }

  earlyReturn(id: string): Observable<void> {
    return this._apiService.postWithSimpleBody(`${this._overDriveUrl}/earlyreturn`, id);
  }

  getDownloadLink(uri: string): Observable<string> {
    return this._apiService.get(`${this._overDriveUrl}/getDownloadRedirectLink/${btoa(uri)}`);
  }

  getItem(id: string): Observable<OverDriveItem> {
    return this._apiService.get(`${this._overDriveUrl}/itemrequest/${id}`);
  }

  placeHold(request: PlaceHold): Observable<OverDrivePlaceHold> {
    return this._apiService.post(`${this._overDriveUrl}/placehold`, request);
  }
}
